import { ANNUAL_PTO_DAYS, TeamMember, Vacation } from '../../types/team.types';

export type PtoSummary = {
  allowance: number;
  remaining: number;
  used: number;
};

// Parse a YYYY-MM-DD string into a UTC date so weekday/interval math never shifts across timezones.
const parseUtcDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1));
};

/**
 * Prorated annual PTO entitlement for a given calendar year.
 * - Joined in an earlier year → full annual allowance.
 * - Joined during the year → allowance for the join month through December (24/yr = 2/month).
 *   e.g. joining in June → months 6–12 = 7 months → round(24 × 7/12) = 14 days.
 * - Joined in a later year → 0 (not yet employed that year).
 */
export const computePtoAllowance = (
  startDate: string,
  year: number,
  annual = ANNUAL_PTO_DAYS,
): number => {
  const [joinYear, joinMonth] = startDate.split('-').map(Number);

  if (!joinYear || !joinMonth) return annual;
  if (joinYear > year) return 0;
  if (joinYear < year) return annual;

  const activeMonths = 12 - joinMonth + 1;
  return Math.round((annual * activeMonths) / 12);
};

// Count working days (Mon–Fri) in an inclusive date range.
export const countWeekdays = (startDate: string, endDate: string): number => {
  const start = parseUtcDate(startDate);
  const end = parseUtcDate(endDate);
  if (end < start) return 0;

  let count = 0;
  for (const cursor = new Date(start); cursor <= end; cursor.setUTCDate(cursor.getUTCDate() + 1)) {
    const weekday = cursor.getUTCDay();
    if (weekday !== 0 && weekday !== 6) count += 1;
  }
  return count;
};

// Working days of a vacation that fall inside the given calendar year (ranges may cross a year edge).
export const countVacationWeekdaysInYear = (
  startDate: string,
  endDate: string,
  year: number,
): number => {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const clampedStart = startDate < yearStart ? yearStart : startDate;
  const clampedEnd = endDate > yearEnd ? yearEnd : endDate;
  if (clampedEnd < clampedStart) return 0;

  return countWeekdays(clampedStart, clampedEnd);
};

// Bucket vacations by member id once, so callers don't re-scan the full list per member.
export const groupVacationsByMember = (vacations: Vacation[]): Map<string, Vacation[]> => {
  const grouped = new Map<string, Vacation[]>();

  for (const vacation of vacations) {
    const existing = grouped.get(vacation.teamMemberId);
    if (existing) existing.push(vacation);
    else grouped.set(vacation.teamMemberId, [vacation]);
  }

  return grouped;
};

// PTO balance for one member in a given year: prorated allowance minus working days already booked.
// `memberVacations` must already be scoped to this member (see groupVacationsByMember).
export const computePtoSummary = (
  member: TeamMember,
  memberVacations: Vacation[],
  year: number,
): PtoSummary => {
  const allowance = computePtoAllowance(member.startDate, year);
  const used = memberVacations.reduce(
    (total, vacation) =>
      total + countVacationWeekdaysInYear(vacation.startDate, vacation.endDate, year),
    0,
  );

  return { allowance, remaining: allowance - used, used };
};
